/**
 * 本地邮箱（Outlook / Foxmail / 网易邮箱大师）拖附件：
 * Windows OLE 走 FileGroupDescriptor。附件只允许 copy，Chrome 默认 move，
 * 若只在虚线框上 preventDefault，客户端会直接拒绝，drop 不会进页面（也就没有提示）。
 * 识别弹窗打开时在 window/document 捕获阶段声明 copy，整个窗口都是合法落点。
 * getAsFile 必须在 drop 同步阶段调用，await 之后 DataTransfer 会失效。
 */
export const EMPTY_FILE_DROP_HINT =
    "未能从邮箱软件读取附件。请先把附件拖到桌面再拖入，或点击「选择文件」";

const LISTENER_OPTS = { capture: true, passive: false };
const dropTargets = new Set();
let pageDropBound = false;

export function allowFileDrop(event) {
    if (!event) {
        return;
    }
    event.preventDefault();
    const dt = event.dataTransfer;
    if (!dt) {
        return;
    }
    try {
        dt.dropEffect = "copy";
    } catch (e) {
        // 部分环境不允许改 dropEffect
    }
}

export const fileDropAllowDirective = {
    inserted(el) {
        const onAllow = (event) => {
            allowFileDrop(event);
        };
        el.addEventListener("dragenter", onAllow, true);
        el.addEventListener("dragover", onAllow, true);
        el.addEventListener("drop", onAllow, true);
        el.__fileDropAllow = onAllow;
    },
    unbind(el) {
        const onAllow = el.__fileDropAllow;
        if (!onAllow) {
            return;
        }
        el.removeEventListener("dragenter", onAllow, true);
        el.removeEventListener("dragover", onAllow, true);
        el.removeEventListener("drop", onAllow, true);
        el.__fileDropAllow = null;
    },
};

function isUsableFile(file) {
    if (!file) {
        return false;
    }
    if (!file.name && !(file.size > 0)) {
        return false;
    }
    return true;
}

function normalizeDroppedFile(file) {
    if (!isUsableFile(file)) {
        return null;
    }
    if (file.name) {
        return file;
    }
    const ext = (file.type && file.type.split("/")[1]) || "bin";
    return new File([file], `attachment.${ext}`, {
        type: file.type || "application/octet-stream",
        lastModified: file.lastModified || Date.now(),
    });
}

export function getDroppedFiles(dataTransfer) {
    if (!dataTransfer) {
        return [];
    }
    const result = [];
    const seen = new Set();
    const directoryNames = new Set();
    const pushFile = (file) => {
        const next = normalizeDroppedFile(file);
        if (!next) {
            return;
        }
        if (directoryNames.has(next.name) && !(next.size > 0)) {
            return;
        }
        const key = `${next.name}|${next.size}|${next.lastModified}`;
        if (seen.has(key)) {
            return;
        }
        seen.add(key);
        result.push(next);
    };
    const items = dataTransfer.items;
    if (items && items.length) {
        for (let i = 0; i < items.length; i += 1) {
            const item = items[i];
            if (!item || item.kind !== "file") {
                continue;
            }
            try {
                const entry = item.webkitGetAsEntry?.();
                if (entry && entry.isDirectory) {
                    directoryNames.add(entry.name);
                    continue;
                }
            } catch (e) {
                // ignore
            }
            if (typeof item.getAsFile === "function") {
                pushFile(item.getAsFile());
            }
        }
    }
    const files = dataTransfer.files;
    if (files && files.length) {
        for (let i = 0; i < files.length; i += 1) {
            pushFile(files[i]);
        }
    }
    return result;
}

export function getDroppedFile(dataTransfer) {
    return getDroppedFiles(dataTransfer)[0] || null;
}

export function warnEmptyFileDrop(vm) {
    if (!vm) {
        return;
    }
    if (vm.$assistantMessage && vm.$assistantMessage.warning) {
        vm.$assistantMessage.warning(EMPTY_FILE_DROP_HINT);
        return;
    }
    if (vm.$message && vm.$message.warning) {
        vm.$message.warning(EMPTY_FILE_DROP_HINT);
    }
}

function distanceToRect(x, y, rect) {
    const dx =
        x < rect.left ? rect.left - x : x > rect.right ? x - rect.right : 0;
    const dy =
        y < rect.top ? rect.top - y : y > rect.bottom ? y - rect.bottom : 0;
    return dx * dx + dy * dy;
}

function hasActiveDropTarget() {
    let active = false;
    dropTargets.forEach((api) => {
        try {
            if (api.shouldHandle()) {
                active = true;
            }
        } catch (e) {
            // ignore
        }
    });
    return active;
}

function pickDropTarget(event) {
    let best = null;
    let bestDist = Infinity;
    let bestArea = Infinity;
    dropTargets.forEach((api) => {
        try {
            if (!api.shouldHandle()) {
                return;
            }
            const el = api.getEl && api.getEl();
            if (!el || typeof el.getBoundingClientRect !== "function") {
                return;
            }
            const rect = el.getBoundingClientRect();
            const area = Math.max(rect.width, 0) * Math.max(rect.height, 0);
            if (area === 0) {
                return;
            }
            const dist = distanceToRect(event.clientX, event.clientY, rect);
            if (dist < bestDist || (dist === bestDist && area < bestArea)) {
                best = api;
                bestDist = dist;
                bestArea = area;
            }
        } catch (e) {
            // ignore
        }
    });
    return best;
}

function setDropTargetHover(activeApi) {
    dropTargets.forEach((api) => {
        try {
            if (api.setHover) {
                api.setHover(api === activeApi);
            }
        } catch (e) {
            // ignore
        }
    });
}

function onPageDragOver(event) {
    if (!hasActiveDropTarget()) {
        return;
    }
    allowFileDrop(event);
    setDropTargetHover(pickDropTarget(event));
}

function onPageDrop(event) {
    if (!hasActiveDropTarget()) {
        return;
    }
    if (event.__pageFileDropHandled) {
        return;
    }
    event.__pageFileDropHandled = true;
    allowFileDrop(event);
    event.stopPropagation();
    const files = getDroppedFiles(event.dataTransfer);
    const target = pickDropTarget(event);
    setDropTargetHover(null);
    if (target && target.onFiles) {
        target.onFiles(files, event);
    }
}

function onPageDragEnd() {
    setDropTargetHover(null);
}

function bindPageFileDrop() {
    if (pageDropBound) {
        return;
    }
    pageDropBound = true;
    document.addEventListener("dragenter", onPageDragOver, LISTENER_OPTS);
    document.addEventListener("dragover", onPageDragOver, LISTENER_OPTS);
    document.addEventListener("drop", onPageDrop, LISTENER_OPTS);
    window.addEventListener("dragenter", onPageDragOver, LISTENER_OPTS);
    window.addEventListener("dragover", onPageDragOver, LISTENER_OPTS);
    window.addEventListener("drop", onPageDrop, LISTENER_OPTS);
    window.addEventListener("dragend", onPageDragEnd, LISTENER_OPTS);
}

function unbindPageFileDrop() {
    if (!pageDropBound || dropTargets.size > 0) {
        return;
    }
    pageDropBound = false;
    document.removeEventListener("dragenter", onPageDragOver, LISTENER_OPTS);
    document.removeEventListener("dragover", onPageDragOver, LISTENER_OPTS);
    document.removeEventListener("drop", onPageDrop, LISTENER_OPTS);
    window.removeEventListener("dragenter", onPageDragOver, LISTENER_OPTS);
    window.removeEventListener("dragover", onPageDragOver, LISTENER_OPTS);
    window.removeEventListener("drop", onPageDrop, LISTENER_OPTS);
    window.removeEventListener("dragend", onPageDragEnd, LISTENER_OPTS);
}

export function registerFileDropTarget(api) {
    dropTargets.add(api);
    bindPageFileDrop();
    return () => {
        dropTargets.delete(api);
        unbindPageFileDrop();
    };
}

export const documentFileDropMixin = {
    mounted() {
        const getter = this.getDocumentFileDropTargets;
        const targets =
            typeof getter === "function" ? getter.call(this) : null;
        if (!targets || !targets.length) {
            return;
        }
        this._unregFileDrops = targets.map((target) =>
            registerFileDropTarget({
                shouldHandle: () => target.shouldHandle.call(this),
                getEl: () => {
                    if (typeof target.getEl === "function") {
                        return target.getEl.call(this);
                    }
                    return this.$refs[target.ref] || null;
                },
                setHover: (hover) => {
                    if (target.hoverKey) {
                        this[target.hoverKey] = hover;
                    }
                },
                onFiles: (files, event) =>
                    target.onFiles.call(this, files, event),
            }),
        );
    },
    beforeDestroy() {
        (this._unregFileDrops || []).forEach((fn) => fn && fn());
        this._unregFileDrops = [];
    },
};
